import React, { useEffect, useState } from 'react';
import { DataGrid, GridCellParams, GridColDef, GridRowsProp, GridToolbar, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'src/store';
import { deleteContact, selectLimit, updateContact } from 'src/features/account/contacts.slice';
import { ContactDrawer } from './ContactDrawer';
import { ContactCreateDialog } from './ContactCreateDialog';
import ConversationCreateDialogV2 from '../Channels/ConversationCreateDialogV2';
import {
  Box,
  IconButton,
  Button,
  useTheme,
  alpha,
  Chip,
  Avatar,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { contactsAdapter, selectContactById } from 'src/features/account/contacts.slice';
import { selectAllFilteredContacts } from 'src/features/account/filteredContacts.slice';
import { setOpenDialog } from 'src/features/theme/theme.slice';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';
import ConversationCallOptions from '../Channels/ConversationCallOptions';
import { useColorMode } from 'src/theme/ThemeProvider';

interface ContactsDataGridProps {
  onSelectionChange: (selectedIds: string[]) => void;
  selectedContactIds: string[];
  currentPage: number;
  onOpenContactDialog?: (contactId: string) => void;
}

const ContactsDataGrid: React.FC<ContactsDataGridProps> = ({
  onSelectionChange,
  selectedContactIds,
  currentPage,
  onOpenContactDialog
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDarkMode = mode === 'dark';
  const { totalContacts } = useAppSelector(state => state.contacts);
  const contactsLoading = useAppSelector(state => state.filteredContacts.contactsLoading);
  const contacts = useAppSelector(selectAllFilteredContacts);
  const [createContactDialog, setCreateContactDialog] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [createConversationOpen, setCreateConversationOpen] = useState(false);
  const [rows, setRows] = useState<GridRowsProp>([]);  // Define state for rows
  const selectedContact = useAppSelector(state => selectContactById(state, selectedContactId));
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(null);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const limit = useAppSelector(selectLimit);

  useEffect(() => {
    // Initialize rows when contacts are fetched
    setRows(contacts.map(contact => ({ ...contact, id: contact.id })));
  }, [contacts]);

  useEffect(() => {
    // Update the selection model when selectedContactIds prop changes
    setRowSelectionModel(selectedContactIds as GridRowId[]);
  }, [selectedContactIds]);

  const handleContactUpdate = (updatedContact) => {
    // Update the row in the rows state
    const updatedRows = rows.map((row) => {
      if (row.id === updatedContact.id) {
        return { ...row, ...updatedContact };
      }
      return row;
    });
    setRows(updatedRows);  // Update rows state
  };

  const handleRowClick = (params) => {
    if (onOpenContactDialog) {
      // Use the parent's function to open the dialog
      onOpenContactDialog(params.id.toString());
    } else {
      // Fallback to the original behavior if parent prop is not provided
      setSelectedContactId(params.id.toString());
      setCreateContactDialog(true);
    }
  };

  const processRowUpdate = async (newRow, oldRow) => {
    try {
      // Dispatch the updateContact action with the updated row data
      await dispatch(updateContact(newRow)).unwrap();
      return newRow;
    } catch (error) {
      console.error('Failed to update contact', error);
      // Return the old row data to revert the changes in the UI
      return oldRow;
    }
  };

  const handleOpenCreateConversation = (params: GridCellParams) => {
    setSelectedContactId(params.id.toString());
    setCreateConversationOpen(true);
  }

  const handleCloseCreateConversation = () => {
    setSelectedContactId(null);
    setCreateConversationOpen(false);
  }

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 90 },
    {
      field: 'starred',
      headerName: '',
      width: 35,
      sortable: true,
      renderCell: (params) => {
        const isStarred = params.row.tags?.includes('isfavorite');
        return (
          <IconButton
            sx={{
              p: '0px',
            }}
            onClick={async (event) => {
              event.stopPropagation();
              const contactId = params.id.toString();
              const currentTags = params.row.tags || [];
              const currentlyIsFavorite = currentTags.includes('isfavorite');

              const updatedTags = currentlyIsFavorite
                ? currentTags.filter((tag: string) => tag !== 'isfavorite')
                : [...currentTags, 'isfavorite'];

              const newFavoriteState = !currentlyIsFavorite;

              try {
                await dispatch(updateContact({
                  contactId,
                  updates: {
                    tags: updatedTags,
                    favorite: newFavoriteState,
                  }
                })).unwrap();
              } catch (error) {
                console.error('Failed to update starred status', error);
              }
            }}
            color={isStarred ? "warning" : "default"}
            size="small"
          >
            {isStarred ? <Star /> : <StarBorder />}
          </IconButton>
        );
      },
    },
    {
      field: 'contact_display',
      headerName: 'Contact',
      width: 360,
      sortable: true,
      editable: false,
      valueGetter: (value, row) => {
        const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim();
        return row.name || (fullName || null) || row.phone_number || row.email || '';
      },
      renderCell: (params) => {
        const row = params.row as any;
        const fullName = `${row.first_name || ''} ${row.last_name || ''}`.trim();
        const primaryText = row.name || (fullName || null) || row.phone_number || row.email || '';

        const phone = row.phone_number as string | undefined;
        const email = row.email as string | undefined;
        const company = row.company_name as string | undefined;

        const contactLine = [phone, email].filter(Boolean).join(' • ');

        const initialsSource: string = (row.name || fullName || email || phone || 'U') as string;
        const initials = initialsSource
          .split(' ')
          .map((s: string) => s[0])
          .join('')
          .slice(0, 2)
          .toUpperCase();

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', height: '100%', overflow: 'hidden' }}>
            <Avatar
              src={row.picture_url || ''}
              alt={primaryText}
              sx={{ width: 30, height: 30, fontSize: 12 }}
            >
              {(!row.picture_url && initials) || null}
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', overflow: 'hidden' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                {primaryText}
              </Typography>
              {company && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {company}
                </Typography>
              )}
              {contactLine && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {contactLine}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'score',
      headerName: 'Score',
      type: 'number',
      width: 100,
      editable: true,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 250,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {params.row.tags?.map((tag: string) => (
            <Chip key={tag} label={tag} size="small" />
          ))}
        </Box>
      ),
    },
    // Removed separate Email, Phone, and Company columns; details are shown in Contact column
    { field: 'status', headerName: 'Status', width: 100, editable: true },
    { field: 'contacted', headerName: 'Contacted', width: 100, editable: true },
    { field: 'shared_notes', headerName: 'Notes', width: 200, editable: true },
    {
      field: 'last_contacted',
      headerName: 'Last Contacted',
      width: 200,
      editable: true,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => (
        <>{params.value ? params.value.toLocaleString() : ''}</>
      )
    },
    {
      field: 'last_comms_from_contact',
      headerName: 'Last Comms (Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_sms_from_contact',
      headerName: 'Last SMS (Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_email_from_contact',
      headerName: 'Last Email (Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_chat_from_contact',
      headerName: 'Last Chat (Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_phone_call_from_contact',
      headerName: 'Last Call (Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_sms_to_contact',
      headerName: 'Last SMS (To Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_email_to_contact',
      headerName: 'Last Email (To Contact)',
      width: 200,
      type: 'dateTime',
      
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_chat_to_contact',
      headerName: 'Last Chat (To Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    {
      field: 'last_phone_call_to_contact',
      headerName: 'Last Call (To Contact)',
      width: 200,
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => <>{params.value ? params.value.toLocaleString() : ''}</>,
    },
    // {
    //   field: 'delete',
    //   headerName: 'Delete',
    //   width: 100,
    //   sortable: false,
    //   renderCell: (params) => (
    //     <IconButton
    //       onClick={(event) => {
    //         event.preventDefault();
    //         event.stopPropagation();
    //         if (window.confirm(`Are you sure you want to delete ${params.row.first_name} ${params.row.last_name} - ${params.row.phone_number}, ${params.row.email}?`)) {
    //           dispatch(deleteContact(params.id.toString()));
    //         }
    //       }}
    //       color="error"
    //     >
    //       <DeleteIcon />
    //     </IconButton>
    //   ),
    // },
    {
      field: 'created_at', headerName: 'Created At', width: 200, editable: true, renderCell: (params) => (
        <>{params.value ? new Date(params.value).toLocaleString() : ''}</>
      )
    },
    {
      field: 'updated_at', headerName: 'Updated At', width: 200, editable: true, renderCell: (params) => (
        <>{params.value ? new Date(params.value).toLocaleString() : ''}</>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              setSelectedContactId(params.id.toString());
              setCallDialogOpen(true);
            }}
            color="primary"
            size="small"
          >
            <PhoneIcon />
          </IconButton>
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              handleOpenCreateConversation(params)
            }}
            color="primary"
            size="small"
          >
            <MessageIcon />
          </IconButton>
          <IconButton
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (window.confirm(`Are you sure you want to delete ${params.row.first_name} ${params.row.last_name} - ${params.row.phone_number}, ${params.row.email}?`)) {
                dispatch(deleteContact(params.id.toString()));
              }
            }}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'metadata', headerName: 'Metadata', width: 200, editable: true, renderCell: (params) => (
        <>{JSON.stringify(params.value)}</>
      )
    },
  ];

  const styles = {
    mainContainer: {
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      flexGrow: 1,
      borderRadius: 3,
      height: '100%',
      position: 'relative',
      backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.background.paper,
    },
    contactsContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '0px',
      p: 0,
      alignItems: 'stretch',
      flexWrap: 'wrap',
      transition: 'all 0.3s ease',
      backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.background.paper,
      flex: 1,
      minHeight: 0, // Important for flex child to respect parent height
    },
    contactsGrid: {
      height: '100%',
      width: '100%',
      '& .MuiDataGrid-root': {
        backgroundColor: isDarkMode ? theme.palette.background.paper : theme.palette.background.default,
        color: isDarkMode ? theme.palette.text.primary : theme.palette.text.primary,
        borderColor: isDarkMode ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
        height: '100%',
      },
      '& .MuiDataGrid-cell': {
        borderColor: 'transparent',
      },
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.05),
        color: isDarkMode ? theme.palette.text.primary : theme.palette.text.primary,
        borderColor: isDarkMode ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
      },
      '& .MuiDataGrid-row': {
        '&:hover': {
          backgroundColor: isDarkMode ? alpha(theme.palette.action.hover, 0.1) : theme.palette.action.hover,
        },
        '&.Mui-selected': {
          backgroundColor: isDarkMode ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.1),
          '&:hover': {
            backgroundColor: isDarkMode ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.2),
          },
        },
        borderBottom: 'none',
      },
      '& .MuiDataGrid-main': {
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.default, 0.3),
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: isDarkMode ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.2),
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: isDarkMode ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.3),
          },
        },
        overflow: 'auto',
        scrollbarWidth: 'none',
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        minHeight: '100%',
      },
      '& .MuiDataGrid-virtualScroller': {
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.3) : alpha(theme.palette.background.default, 0.3),
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: isDarkMode ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.2),
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: isDarkMode ? alpha(theme.palette.primary.main, 0.3) : alpha(theme.palette.primary.main, 0.3),
          },
        },
        overflow: 'auto !important',
        scrollbarWidth: 'none',
      },
      '& .MuiDataGrid-footerContainer': {
        backgroundColor: isDarkMode ? theme.palette.background.paper : theme.palette.background.default,
        borderColor: isDarkMode ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
      },
      '& .MuiDataGrid-toolbarContainer': {
        backgroundColor: isDarkMode ? theme.palette.background.paper : theme.palette.background.default,
        borderColor: isDarkMode ? alpha(theme.palette.divider, 0.1) : theme.palette.divider,
        '& .MuiButton-root': {
          color: isDarkMode ? theme.palette.text.secondary : theme.palette.text.primary,
        },
      },
      '& .MuiCheckbox-root': {
        color: isDarkMode ? theme.palette.text.secondary : theme.palette.text.primary,
      },
      '& .MuiTablePagination-root': {
        color: isDarkMode ? theme.palette.text.primary : theme.palette.text.primary,
      },
      '& .MuiDataGrid-menuListItem .MuiListItemIcon-root + .MuiListItemText-root': {
        marginLeft: theme.spacing(1.5),
      },
    },
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={styles.mainContainer}>
        <Box sx={styles.contactsContainer}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            rowHeight={62}
            initialState={{
              pagination: {
                paginationModel: {
                  page: 1,
                  pageSize: limit
                }
              },
              columns: {
                columnVisibilityModel: {
                  id: false,
                  picture_url: false,
                  last_contacted: false,
                  last_comms_from_contact: false,
                  last_sms_from_contact: false,
                  last_email_from_contact: false,
                  last_chat_from_contact: false,
                  last_phone_call_from_contact: false,
                  last_sms_to_contact: false,
                  last_email_to_contact: false,
                  last_chat_to_contact: false,
                  last_phone_call_to_contact: false,
                  metadata: false,
                },
              },
            }}
            sx={styles.contactsGrid}
            loading={contactsLoading}
            processRowUpdate={processRowUpdate}
            onRowClick={handleRowClick}
            paginationMode="server"
            paginationModel={{
              page: currentPage,
              pageSize: limit
            }}
            rowCount={totalContacts}
            slots={{
              toolbar: () => null,
              pagination: () => null,
            }}
            checkboxSelection
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
              const selectedIds = newRowSelectionModel.map((id) => id.toString());
              onSelectionChange(selectedIds);
            }}
            pageSizeOptions={[10, 25, 50, 100, 500, 1000]}
            disableRowSelectionOnClick
            hideFooter
          />
        </Box>
      </Box>

      {!onOpenContactDialog && selectedContactId && (
        <ContactDrawer
          open={createContactDialog}
          onClose={() => setCreateContactDialog(false)}
          contactId={selectedContact.id}
        />
      )}

      {selectedContact && createConversationOpen && (
        <ConversationCreateDialogV2
          open={createConversationOpen}
          handleClose={handleCloseCreateConversation}
          incomingDraftMessage={""}
          incomingContactId={selectedContact.id}
          incomingChannelId={selectedChannelId || ""}
        />
      )}

      <ConversationCallOptions
        open={callDialogOpen}
        channelId={selectedChannelId}
        incomingContactId={selectedContactId}
        onClose={() => setCallDialogOpen(false)}
      />
    </Box>
  );
};

export default ContactsDataGrid;
